/**
 * UpgradeModal Component
 *
 * Modal for upgrading a free user to Premium subscription.
 * Integrates with PayPal for payment processing.
 *
 * Features:
 * - Premium benefits overview
 * - Pricing information
 * - Choice between recurring subscription and one-time payment
 * - PayPal payment integration
 * - Terms and conditions acceptance
 * - Success confirmation after payment
 * - Error handling for payment failures
 * - Loading states during payment processing
 *
 * @validates Requirements 1.2, 1.3, 2.1-2.5, 3.1-3.5, 5.1-5.4, 6.1-6.5, 7.2, 7.3, 7.5
 */

import { useState, useCallback, useEffect, useMemo } from 'react';
import { useStore } from '../../store';
import { TermsAcceptance } from './TermsAcceptance';
import { PayPalButton, PayPalOrderData } from './PayPalButton';
import { PayPalSubscriptionButton, PayPalSubscriptionData } from './PayPalSubscriptionButton';
import { PaymentTypeSelector, PaymentType } from './PaymentTypeSelector';
import { usePayPalScript } from '../../hooks/usePayPalScript';
import { SubscriptionType } from '@spellion/shared';

/**
 * Props for the UpgradeModal component
 */
export interface UpgradeModalProps {
  /** Whether the modal is open */
  isOpen: boolean;
  /** Callback when the modal should close */
  onClose: () => void;
  /** Callback when upgrade is successful */
  onSuccess?: () => void;
}

/**
 * Internal state for the upgrade process
 */
type UpgradeState = 'idle' | 'loading' | 'success' | 'error';

/**
 * Premium benefits list
 * @param freeUserDailyQuota - The daily quota for free users
 */
function getPremiumBenefits(freeUserDailyQuota: number) {
  return [
    {
      icon: '🔓',
      title: 'Recherches illimitées',
      description: `Plus de limite quotidienne de ${freeUserDailyQuota} recherches`,
    },
    {
      icon: '📚',
      title: 'Tous les dictionnaires',
      description: 'Accédez à tous les dictionnaires et importez les vôtres',
    },
    {
      icon: '⚡',
      title: 'Priorité serveur',
      description: 'Recherches plus rapides en période de forte affluence',
    },
  ];
}

/**
 * UpgradeModal Component
 *
 * Displays a modal for upgrading to Premium subscription.
 * Handles the full payment flow with PayPal integration.
 *
 * @example
 * ```tsx
 * <UpgradeModal
 *   isOpen={showUpgradeModal}
 *   onClose={() => setShowUpgradeModal(false)}
 *   onSuccess={() => console.log('Upgraded!')}
 * />
 * ```
 */
export function UpgradeModal({ isOpen, onClose, onSuccess }: UpgradeModalProps) {
  // State management
  const [state, setState] = useState<UpgradeState>('idle');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  
  // PayPal integration states
  const [termsAccepted, setTermsAccepted] = useState<boolean>(false);
  const [paymentProcessing, setPaymentProcessing] = useState<boolean>(false);
  const [paymentType, setPaymentType] = useState<PaymentType>('subscription');
  
  // Get config and user from store
  const freeUserDailyQuota = useStore((state) => state.freeUserDailyQuota);
  const premiumMonthlyPrice = useStore((state) => state.premiumMonthlyPrice);
  const premiumSubscriptionPrice = useStore((state) => state.premiumSubscriptionPrice);
  const paypalClientId = useStore((state) => state.paypalClientId);
  const paypalPlanId = useStore((state) => state.paypalPlanId);
  const loadPublicConfig = useStore((state) => state.loadPublicConfig);
  const token = useStore((state) => state.token);
  const user = useStore((state) => state.user);
  const setUser = useStore((state) => state.setUser);

  // Check if subscription option is available
  const subscriptionAvailable = !!paypalPlanId && paypalPlanId.trim() !== '';

  // PayPal SDK loading state - uses clientId from store (loaded dynamically from config)
  const { isLoaded: paypalLoaded, isLoading: paypalLoading, error: paypalError } = usePayPalScript(paypalClientId);

  // Memoized pricing info
  const premiumPricing = useMemo(() => ({
    oneTimePrice: premiumMonthlyPrice,
    subscriptionPrice: premiumSubscriptionPrice,
    currency: '€',
    period: 'mois',
  }), [premiumMonthlyPrice, premiumSubscriptionPrice]);

  // Load public config when modal opens (force reload to get latest price)
  useEffect(() => {
    if (isOpen) {
      loadPublicConfig(true);
    }
  }, [isOpen, loadPublicConfig]);

  // Reset state when modal opens/closes
  useEffect(() => {
    if (!isOpen) {
      // Reset state after a delay to allow for smooth close animation
      const timer = setTimeout(() => {
        setState('idle');
        setErrorMessage(null);
        setTermsAccepted(false);
        setPaymentProcessing(false);
        // Reset to subscription if available, otherwise one-time
        setPaymentType(subscriptionAvailable ? 'subscription' : 'one-time');
      }, 300);
      return () => clearTimeout(timer);
    } else {
      // When modal opens, default to subscription if available
      setPaymentType(subscriptionAvailable ? 'subscription' : 'one-time');
    }
  }, [isOpen, subscriptionAvailable]);

  /**
   * Handle PayPal payment success (one-time payment)
   * Calls backend to verify payment, updates local state, shows success
   * @validates Requirements 3.2, 3.3, 3.4, 3.5
   */
  const handlePayPalSuccess = useCallback(async (orderData: PayPalOrderData) => {
    if (!token || !user) {
      setErrorMessage('Session expirée. Veuillez vous reconnecter.');
      setState('error');
      return;
    }

    setPaymentProcessing(true);

    try {
      // Call backend to verify payment
      const response = await fetch('/api/subscription/upgrade-paypal', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          orderId: orderData.orderId,
          captureId: orderData.captureId,
          amount: orderData.amount,
          paymentType: 'one-time',
        }),
      });

      const result = await response.json();

      if (!response.ok || !result.success) {
        throw new Error(result.error?.message || 'La vérification du paiement a échoué');
      }

      // Update local user state to reflect Premium status
      setUser({
        ...user,
        subscriptionType: SubscriptionType.PREMIUM,
      });

      // Show success state
      setState('success');
      
      // Call onSuccess callback if provided
      if (onSuccess) {
        onSuccess();
      }
    } catch (error) {
      console.error('Payment verification error:', error);
      setErrorMessage(
        error instanceof Error
          ? error.message
          : 'Le paiement n\'a pas pu être vérifié'
      );
      setState('error');
    } finally {
      setPaymentProcessing(false);
    }
  }, [token, user, setUser, onSuccess]);

  /**
   * Handle PayPal subscription success (recurring payment)
   * Calls backend to activate subscription, updates local state, shows success
   */
  const handleSubscriptionSuccess = useCallback(async (subscriptionData: PayPalSubscriptionData) => {
    if (!token || !user) {
      setErrorMessage('Session expirée. Veuillez vous reconnecter.');
      setState('error');
      return;
    }

    setPaymentProcessing(true);

    try {
      // Call backend to activate subscription
      const response = await fetch('/api/subscription/activate-subscription', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          subscriptionId: subscriptionData.subscriptionId,
          planId: subscriptionData.planId,
          paymentType: 'subscription',
        }),
      });

      const result = await response.json();

      if (!response.ok || !result.success) {
        throw new Error(result.error?.message || 'L\'activation de l\'abonnement a échoué');
      }

      // Update local user state to reflect Premium status
      setUser({
        ...user,
        subscriptionType: SubscriptionType.PREMIUM,
      });

      // Show success state
      setState('success');
      
      // Call onSuccess callback if provided
      if (onSuccess) {
        onSuccess();
      }
    } catch (error) {
      console.error('Subscription activation error:', error);
      setErrorMessage(
        error instanceof Error
          ? error.message
          : 'L\'abonnement n\'a pas pu être activé'
      );
      setState('error');
    } finally {
      setPaymentProcessing(false);
    }
  }, [token, user, setUser, onSuccess]);

  /**
   * Handle PayPal payment cancellation
   * Shows cancellation message and returns to options
   * @validates Requirement 5.1
   */
  const handlePayPalCancel = useCallback(() => {
    setErrorMessage('Le paiement a été annulé');
    setState('error');
  }, []);

  /**
   * Handle PayPal payment error
   * Shows error message with retry option
   * @validates Requirements 5.2, 5.3
   */
  const handlePayPalError = useCallback((error: Error) => {
    console.error('PayPal error:', error);
    setErrorMessage(
      error.message || 'Une erreur technique est survenue. Veuillez réessayer.'
    );
    setState('error');
  }, []);

  /**
   * Handle closing the modal
   */
  const handleClose = useCallback(() => {
    if (state === 'loading' || paymentProcessing) {
      // Don't allow closing during payment processing
      return;
    }
    onClose();
  }, [state, paymentProcessing, onClose]);

  /**
   * Handle retry after error
   */
  const handleRetry = useCallback(() => {
    setState('idle');
    setErrorMessage(null);
    setTermsAccepted(false);
    setPaymentProcessing(false);
    // Reset to subscription if available, otherwise one-time
    setPaymentType(subscriptionAvailable ? 'subscription' : 'one-time');
  }, [subscriptionAvailable]);

  // Don't render if not open
  if (!isOpen) {
    return null;
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="upgrade-modal-title"
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm transition-opacity"
        onClick={handleClose}
        aria-hidden="true"
      />

      {/* Modal content */}
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden animate-scale-in">
        {/* Close button */}
        {state !== 'loading' && !paymentProcessing && (
          <button
            onClick={handleClose}
            className="absolute top-4 right-4 p-2 text-slate-400 hover:text-slate-600 transition-colors rounded-lg hover:bg-slate-100"
            aria-label="Fermer"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        )}

        {/* Render content based on state */}
        {state === 'success' ? (
          <SuccessContent onClose={onClose} />
        ) : state === 'error' ? (
          <ErrorContent
            errorMessage={errorMessage}
            onRetry={handleRetry}
            onClose={onClose}
          />
        ) : (
          <UpgradeContent
            isLoading={state === 'loading'}
            onClose={handleClose}
            freeUserDailyQuota={freeUserDailyQuota}
            premiumPricing={premiumPricing}
            termsAccepted={termsAccepted}
            setTermsAccepted={setTermsAccepted}
            paypalLoading={paypalLoading}
            paypalLoaded={paypalLoaded}
            paypalError={paypalError}
            paymentProcessing={paymentProcessing}
            userId={user?.id || ''}
            paymentType={paymentType}
            setPaymentType={setPaymentType}
            paypalPlanId={paypalPlanId}
            subscriptionAvailable={subscriptionAvailable}
            onPayPalSuccess={handlePayPalSuccess}
            onSubscriptionSuccess={handleSubscriptionSuccess}
            onPayPalCancel={handlePayPalCancel}
            onPayPalError={handlePayPalError}
          />
        )}
      </div>
    </div>
  );
}

/**
 * Props for UpgradeContent component
 */
interface UpgradeContentProps {
  isLoading: boolean;
  onClose: () => void;
  freeUserDailyQuota: number;
  premiumPricing: { oneTimePrice: number; subscriptionPrice: number; currency: string; period: string };
  termsAccepted: boolean;
  setTermsAccepted: (accepted: boolean) => void;
  paypalLoading: boolean;
  paypalLoaded: boolean;
  paypalError: Error | null;
  paymentProcessing: boolean;
  userId: string;
  paymentType: PaymentType;
  setPaymentType: (type: PaymentType) => void;
  paypalPlanId: string;
  subscriptionAvailable: boolean;
  onPayPalSuccess: (orderData: PayPalOrderData) => void;
  onSubscriptionSuccess: (subscriptionData: PayPalSubscriptionData) => void;
  onPayPalCancel: () => void;
  onPayPalError: (error: Error) => void;
}

/**
 * Main upgrade content with benefits and payment button
 */
function UpgradeContent({
  isLoading,
  onClose,
  freeUserDailyQuota,
  premiumPricing,
  termsAccepted,
  setTermsAccepted,
  paypalLoading,
  paypalLoaded,
  paypalError,
  paymentProcessing,
  userId,
  paymentType,
  setPaymentType,
  paypalPlanId,
  subscriptionAvailable,
  onPayPalSuccess,
  onSubscriptionSuccess,
  onPayPalCancel,
  onPayPalError,
}: UpgradeContentProps) {
  const premiumBenefits = getPremiumBenefits(freeUserDailyQuota);
  
  // Determine if the modal should be in a disabled state
  const isDisabled = isLoading || paypalLoading || paymentProcessing;

  // Determine the description based on payment type
  const displayPrice = paymentType === 'subscription' 
    ? premiumPricing.subscriptionPrice 
    : premiumPricing.oneTimePrice;
  const pricingDescription = paymentType === 'subscription'
    ? 'Renouvellement automatique, annulable à tout moment'
    : 'Paiement unique pour 1 mois, renouvellement manuel';
  
  return (
    <>
      {/* Header with gradient */}
      <div className="bg-gradient-to-br from-primary-500 to-secondary-600 px-6 py-8 text-white text-center">
        <div className="inline-flex items-center justify-center w-16 h-16 bg-white/20 rounded-full mb-4">
          <span className="text-3xl">⭐</span>
        </div>
        <h2 id="upgrade-modal-title" className="text-2xl font-bold mb-2">
          Passez à Premium
        </h2>
        <p className="text-primary-100">
          Débloquez toutes les fonctionnalités de Spellion
        </p>
      </div>

      {/* Pricing */}
      <div className="px-6 py-6 border-b border-slate-100">
        <div className="flex items-baseline justify-center gap-1">
          <span className="text-4xl font-bold text-slate-900">
            {displayPrice.toFixed(2).replace('.', ',')}
          </span>
          <span className="text-xl text-slate-600">
            {premiumPricing.currency}
          </span>
          <span className="text-slate-500">/ {premiumPricing.period}</span>
        </div>
        <p className="text-sm text-slate-500 text-center mt-2">
          {pricingDescription}
        </p>
      </div>

      {/* Benefits list */}
      <div className="px-6 py-6">
        <ul className="space-y-4">
          {premiumBenefits.map((benefit, index) => (
            <li key={index} className="flex items-start gap-3">
              <span className="text-xl flex-shrink-0">{benefit.icon}</span>
              <div>
                <p className="font-medium text-slate-900">{benefit.title}</p>
                <p className="text-sm text-slate-500">{benefit.description}</p>
              </div>
            </li>
          ))}
        </ul>
      </div>

      {/* Action buttons */}
      <div className="px-6 py-6 bg-slate-50 border-t border-slate-100">
        {/* PayPal SDK Loading State */}
        {paypalLoading && (
          <div className="flex items-center justify-center gap-2 py-4 text-slate-500 text-sm">
            <LoadingSpinner />
            <span>Chargement du service de paiement...</span>
          </div>
        )}

        {/* PayPal SDK Error State with retry option */}
        {paypalError && !paypalLoading && (
          <div className="text-center py-4">
            <div className="text-red-600 text-sm mb-3">
              <p className="font-medium">Service de paiement indisponible</p>
              <p className="text-red-500 mt-1">{paypalError.message}</p>
            </div>
            <button
              onClick={() => window.location.reload()}
              className="py-2 px-4 bg-primary-600 text-white text-sm font-medium rounded-lg hover:bg-primary-700 transition-colors"
            >
              Réessayer
            </button>
          </div>
        )}

        {/* Payment Processing State */}
        {paymentProcessing && (
          <div className="flex items-center justify-center gap-2 py-4 text-slate-500 text-sm">
            <LoadingSpinner />
            <span>Traitement du paiement en cours...</span>
          </div>
        )}

        {/* Terms Acceptance and PayPal Button */}
        {paypalLoaded && !paypalError && !paymentProcessing && (
          <>
            {/* Payment Type Selector */}
            <div className="mb-4">
              <PaymentTypeSelector
                selected={paymentType}
                onSelect={setPaymentType}
                oneTimePrice={premiumPricing.oneTimePrice}
                subscriptionPrice={premiumPricing.subscriptionPrice}
                disabled={isDisabled}
                subscriptionAvailable={subscriptionAvailable}
              />
            </div>

            {/* Terms Acceptance Component */}
            <div className="mb-4">
              <TermsAcceptance
                checked={termsAccepted}
                onChange={setTermsAccepted}
                disabled={isDisabled}
              />
            </div>

            {/* PayPal Subscription Button - shown only when terms accepted and subscription selected */}
            {termsAccepted && userId && paymentType === 'subscription' && subscriptionAvailable && (
              <div className="mb-3">
                <PayPalSubscriptionButton
                  userId={userId}
                  planId={paypalPlanId}
                  onSuccess={onSubscriptionSuccess}
                  onCancel={onPayPalCancel}
                  onError={onPayPalError}
                  enabled={termsAccepted && !isDisabled}
                />
              </div>
            )}

            {/* PayPal One-Time Button - shown only when terms accepted and one-time selected */}
            {termsAccepted && userId && paymentType === 'one-time' && (
              <div className="mb-3">
                <PayPalButton
                  userId={userId}
                  onSuccess={onPayPalSuccess}
                  onCancel={onPayPalCancel}
                  onError={onPayPalError}
                  enabled={termsAccepted && !isDisabled}
                />
              </div>
            )}

            {/* Message when terms not accepted */}
            {!termsAccepted && (
              <p className="text-center text-slate-500 text-sm py-4">
                Acceptez les conditions pour afficher les options de paiement
              </p>
            )}
          </>
        )}

        <button
          onClick={onClose}
          disabled={isDisabled}
          className="w-full mt-3 py-2 px-4 text-slate-600 hover:text-slate-900 transition-colors text-sm disabled:opacity-50"
        >
          Non merci, peut-être plus tard
        </button>

        {/* Payment security notice */}
        <p className="text-xs text-slate-400 text-center mt-4 flex items-center justify-center gap-1">
          <LockIcon />
          Paiement sécurisé par PayPal
        </p>
      </div>
    </>
  );
}

/**
 * Props for SuccessContent component
 */
interface SuccessContentProps {
  onClose: () => void;
}

/**
 * Success confirmation content after payment
 * Validates Requirement 9.5: Confirmation after successful subscription
 */
function SuccessContent({ onClose }: SuccessContentProps) {
  return (
    <div className="px-6 py-12 text-center">
      {/* Success icon with animation */}
      <div className="inline-flex items-center justify-center w-20 h-20 bg-emerald-100 rounded-full mb-6 animate-bounce-once">
        <svg
          className="w-10 h-10 text-emerald-600"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2}
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M5 13l4 4L19 7"
          />
        </svg>
      </div>

      <h2 className="text-2xl font-bold text-slate-900 mb-2">
        Bienvenue dans Premium !
      </h2>

      <p className="text-slate-600 mb-6">
        Votre abonnement est maintenant actif. Un email de confirmation vous a été envoyé.
      </p>

      <div className="bg-emerald-50 rounded-xl p-4 mb-6">
        <h3 className="font-semibold text-emerald-800 mb-2">
          Ce que vous pouvez faire maintenant :
        </h3>
        <ul className="text-sm text-emerald-700 space-y-1">
          <li>✓ Recherches illimitées</li>
          <li>✓ Accès à tous les dictionnaires de l'application</li>
          <li>✓ Importez vos propres listes de mots</li>
        </ul>
      </div>

      <button
        onClick={onClose}
        className="w-full py-3 px-4 bg-primary-600 text-white font-semibold rounded-xl hover:bg-primary-700 transition-all duration-200"
      >
        Commencer à utiliser Premium
      </button>
    </div>
  );
}

/**
 * Props for ErrorContent component
 */
interface ErrorContentProps {
  errorMessage: string | null;
  onRetry: () => void;
  onClose: () => void;
}

/**
 * Error content when payment fails
 * Validates Requirement 9.7: Display error message on payment failure, retain Free_User status
 */
function ErrorContent({ errorMessage, onRetry, onClose }: ErrorContentProps) {
  return (
    <div className="px-6 py-12 text-center">
      {/* Error icon */}
      <div className="inline-flex items-center justify-center w-20 h-20 bg-red-100 rounded-full mb-6">
        <svg
          className="w-10 h-10 text-red-600"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2}
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M6 18L18 6M6 6l12 12"
          />
        </svg>
      </div>

      <h2 className="text-2xl font-bold text-slate-900 mb-2">
        Échec du paiement
      </h2>

      <p className="text-slate-600 mb-6">
        {errorMessage || 'Le paiement n\'a pas pu être traité. Votre compte reste gratuit.'}
      </p>

      <div className="bg-red-50 rounded-xl p-4 mb-6 text-sm text-red-700">
        <p className="font-medium mb-1">Causes possibles :</p>
        <ul className="list-disc list-inside text-left">
          <li>Carte bancaire refusée</li>
          <li>Fonds insuffisants</li>
          <li>Problème de connexion</li>
        </ul>
      </div>

      <div className="space-y-3">
        <button
          onClick={onRetry}
          className="w-full py-3 px-4 bg-primary-600 text-white font-semibold rounded-xl hover:bg-primary-700 transition-all duration-200"
        >
          Réessayer
        </button>

        <button
          onClick={onClose}
          className="w-full py-2 px-4 text-slate-600 hover:text-slate-900 transition-colors text-sm"
        >
          Annuler
        </button>
      </div>
    </div>
  );
}

/**
 * Loading spinner component
 */
function LoadingSpinner() {
  return (
    <svg
      className="animate-spin h-5 w-5 text-white"
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
    >
      <circle
        className="opacity-25"
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="4"
      />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
      />
    </svg>
  );
}

/**
 * Lock icon for security notice
 */
function LockIcon() {
  return (
    <svg
      className="w-3 h-3"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
      />
    </svg>
  );
}

export default UpgradeModal;
